import type { AuthSession } from '../auth/firebaseAuth';

/**
 * The signed-in account: live in memory, and — once this browser is remembered
 * — resumable from a stored refresh token.
 *
 * The original rule here was that nothing about the account survives the tab,
 * because a refresh token is a long-lived credential for the whole account. That
 * rule still holds for a browser nobody has trusted: it stores nothing, exactly
 * as before. What changed is that "remember this browser" now means something,
 * and a machine already holding a sealed copy of the VAULT key is not made
 * meaningfully safer by refusing to hold the lesser credential beside it. The
 * cost of refusing was real — every reload asked for a sign-in already given.
 *
 * So the storage is tied to the trust decision rather than taken on its own:
 *
 * - Written only by [persistAccount], which the connect flow calls once the
 *   browser is actually remembered.
 * - Dropped by [clearPersistedAccount] the moment it is forgotten, so the token
 *   never outlives the trust record it was granted alongside.
 * - The refresh token ONLY. An id_token is a short-lived bearer credential with
 *   no business on disk, and a fresh one costs a single call.
 *
 * What this does NOT do is weaken a guard. Resuming the account does not open
 * the vault: a phrase-guarded browser still asks for the phrase, a
 * hardware-guarded one still asks for the authenticator. It removes the sign-in
 * step and nothing else. A `none`-guarded browser does open straight away —
 * which is what choosing `none` already meant.
 *
 * Someone who lifts this token off the disk gets the ACCOUNT, not the vault: the
 * sealed key still needs its share, and a forgotten browser has no share to get.
 */

const REFRESH_KEY = 'pondering.account.refresh';

let current: AuthSession | null = null;

export function getAuth(): AuthSession | null {
  return current;
}

export function setAuth(session: AuthSession | null): void {
  current = session;
  // Refresh tokens rotate: the endpoint usually hands back a new one, and a
  // stored copy that does not keep up is a cold load asking to sign in again.
  // An update only — this never starts persisting on its own.
  if (session && read() !== null) write(session.refreshToken);
}

/** Start persisting, because this browser is now remembered. */
export function persistAccount(session: AuthSession): void {
  write(session.refreshToken);
}

export function clearPersistedAccount(): void {
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* a browser refusing storage has nothing to clear */
  }
}

export function persistedRefreshToken(): string | null {
  return read();
}

function read(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function write(token: string): void {
  try {
    localStorage.setItem(REFRESH_KEY, token);
  } catch {
    /* private mode and quota refusals cost a sign-in, not a failure */
  }
}
